import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { PageBody } from '@/components/AppShell'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { adminApi } from '@/lib/api'
import { cn } from '@/lib/utils'

/**
 * Imported report versions. The file comes from
 * `scripts/export_web_dataset.py`; uploading the same one twice is a no-op,
 * uploading a changed one adds a version and leaves the old links alone.
 */
export function DatasetsPage() {
  const client = useQueryClient()
  const datasets = useQuery({ queryKey: ['admin-datasets'], queryFn: adminApi.datasets })
  const fileInput = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)

  const upload = useMutation({
    mutationFn: async (file: File) =>
      adminApi.importDataset(JSON.parse(await file.text())),
    onSuccess: (result) => {
      toast.success(
        result.reused
          ? `${result.slug} v${result.version} był już wczytany`
          : `Zaimportowano ${result.slug} v${result.version} (${result.boards} szachownic)`,
      )
      void client.invalidateQueries({ queryKey: ['admin-datasets'] })
    },
    onError: (error) =>
      toast.error('Import się nie powiódł', {
        description: error instanceof Error ? error.message : undefined,
      }),
    onSettled: () => setBusy(false),
  })

  /**
   * The name and the revision are what a reviewer reads in their header, so
   * they are editable here rather than only at export time. Everything the
   * boards are keyed by stays as imported.
   */
  const rename = useMutation({
    mutationFn: ({ id, ...input }: { id: string; label?: string; revision?: string }) =>
      adminApi.renameDataset(id, input),
    onSuccess: () => {
      toast.success('Nazwa zapisana')
      void client.invalidateQueries({ queryKey: ['admin-datasets'] })
      void client.invalidateQueries({ queryKey: ['admin-invites'] })
    },
    onError: (error) =>
      toast.error('Nie udało się zmienić nazwy', {
        description: error instanceof Error ? error.message : undefined,
      }),
  })

  const rows = datasets.data ?? []

  return (
    <PageBody>
      <Card>
        <CardHeader>
          <CardTitle>Zbiory danych</CardTitle>
          <CardDescription>
            Wyeksportowany raport w formacie JSON, jeden plik na wersję.
          </CardDescription>
          <CardAction>
            <input
              ref={fileInput}
              type="file"
              accept="application/json"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0]
                if (file) {
                  setBusy(true)
                  upload.mutate(file)
                }
                event.target.value = ''
              }}
            />
            <Button onClick={() => fileInput.current?.click()} disabled={busy}>
              {busy ? 'Wczytywanie…' : 'Wczytaj zbiór'}
            </Button>
          </CardAction>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <Empty>
              <EmptyHeader>
                <EmptyTitle>Nie ma jeszcze żadnego zbioru</EmptyTitle>
                <EmptyDescription>
                  Wczytaj plik wyeksportowany przez scripts/export_web_dataset.py.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nazwa</TableHead>
                  <TableHead>Wersja</TableHead>
                  <TableHead className="text-right">Osie</TableHead>
                  <TableHead className="text-right">Szachownice</TableHead>
                  <TableHead className="text-right">Partie</TableHead>
                  <TableHead>Wczytany</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((dataset) => (
                  <TableRow key={dataset.id}>
                    <TableCell className="max-w-[260px]">
                      <EditableCell
                        value={dataset.label}
                        className="font-medium"
                        onSave={(label) => rename.mutate({ id: dataset.id, label })}
                      />
                      <EditableCell
                        value={dataset.revision}
                        className="text-xs text-muted-foreground"
                        onSave={(revision) => rename.mutate({ id: dataset.id, revision })}
                      />
                      <span className="block px-1 font-mono text-[11px] text-faint">
                        {dataset.slug}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-2">
                        v{dataset.version}
                        <Badge variant="secondary">
                          {dataset.layout === 'print' ? 'druk' : 'ekran'}
                        </Badge>
                      </span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {dataset.axes}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {dataset.totalBoards}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {dataset.games}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {dataset.importedAt}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </PageBody>
  )
}

/**
 * Click to edit, Enter or blur to save, Escape to give up.
 *
 * A dialog for two short strings would be more ceremony than the edit
 * deserves, and the table is where the value is read anyway.
 */
function EditableCell({
  value,
  className,
  onSave,
}: {
  value: string
  className?: string
  onSave: (value: string) => void
}) {
  const [draft, setDraft] = useState(value)
  const [editing, setEditing] = useState(false)

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => {
          setDraft(value)
          setEditing(true)
        }}
        title="Kliknij, aby zmienić"
        className={cn(
          'block w-full truncate rounded px-1 py-0.5 text-left hover:bg-muted',
          className,
        )}
      >
        {value}
      </button>
    )
  }

  const commit = () => {
    setEditing(false)
    const next = draft.trim()
    if (next && next !== value) onSave(next)
  }

  return (
    <Input
      autoFocus
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') commit()
        if (event.key === 'Escape') setEditing(false)
      }}
      className={cn('h-7 px-1 py-0', className)}
    />
  )
}
